import { useRef, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useMutation } from '@tanstack/react-query'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { resolvePlaceSearch } from 'tripPlanner/api'
import { isNetworkError } from 'tripPlanner/utils/networkError'
import { usePlaceSearchFailure } from 'tripPlanner/assistant/contexts/PlaceSearchFailureContext'

/** Returns true only for a confirmed backend 404 — place truly does not exist. */
function isNotFoundError(error: unknown): error is { status: number } {
  return (
    error !== null &&
    typeof error === 'object' &&
    'status' in error &&
    typeof error.status === 'number' &&
    error.status === 404
  )
}

// The controller is included in mutation variables so onSuccess/onError can compare
// against their own request's signal — not the latest ref value.
type ResolveArgs = {
  searchQuery: string
  placeName: string
  controller: AbortController
}

type PublicResolveArgs = {
  searchQuery: string
  placeName: string
}

/**
 * Resolves a place-search: link to a Google Place ID and opens the place drawer.
 * Implements two-strike demotion: first failure shows a snackbar, second failure
 * permanently demotes the tag to plain text via PlaceSearchFailureContext.
 *
 * Each call to resolvePlace aborts any previous in-flight request (last-click-wins).
 * cancel() aborts the current request — call on reset_chat_state to prevent
 * stale resolves from reopening the drawer after a chat switch.
 */
export function usePlaceSearchResolver(onResolved: (googlePlaceId: string, placeName: string) => void) {
  const intl = useIntl()
  const { firstFailedQueries, markFirstFailed, markPermanentlyFailed } = usePlaceSearchFailure()
  const abortControllerRef = useRef<AbortController | null>(null)

  const mutation = useMutation({
    mutationFn: async({ searchQuery, controller }: ResolveArgs) => {
      return resolvePlaceSearch(searchQuery, undefined, controller.signal)
    },
    onSuccess: (data, { placeName, controller }) => {
      // Guard against this specific request's signal, not the latest ref
      if (controller.signal.aborted) return
      onResolved(data.googlePlaceId, placeName)
    },
    onError: (error, { searchQuery: rawQuery, controller }) => {
      if (controller.signal.aborted) return
      // Normalise key to match what resolvePlaceSearch sends to the backend
      const searchQuery = rawQuery.trim()
      // Transient errors (network, 5xx) show a snackbar but don't count toward demotion —
      // the place may resolve fine once the backend recovers. Only a confirmed 404
      // means the query truly can't be resolved and should count toward permanent demotion.
      if (isNetworkError(error) || !isNotFoundError(error)) {
        showSnackbar(intl.formatMessage({
          id: 'nfHCQkR24ieljVK1K1du',
          defaultMessage: "Couldn't find this place",
          description: 'Snackbar shown when a place-search link in chat cannot be resolved',
        }), 'critical')
        return
      }
      if (firstFailedQueries.has(searchQuery)) {
        markPermanentlyFailed(searchQuery)
      } else {
        markFirstFailed(searchQuery)
        showSnackbar(intl.formatMessage({
          id: 'nfHCQkR24ieljVK1K1du',
          defaultMessage: "Couldn't find this place",
          description: 'Snackbar shown when a place-search link in chat cannot be resolved',
        }), 'critical')
      }
    },
  })

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort()
  }, [])

  const resolvePlace = useCallback(({ searchQuery, placeName }: PublicResolveArgs) => {
    // Abort any previous in-flight request — ensures last-click-wins
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller
    mutation.mutate({ searchQuery, placeName, controller })
  }, [mutation])

  return { resolvePlace, cancel }
}
